import { useState } from "react"

// UI
import { Button } from "@/components/Button"
import { Heading } from "@/components/Typography"
import { DialogWrapper } from "@/components/dialogs/DialogWrapper"
import { StarRating } from "@/components/StarRating"

// Icons
import { XIcon } from "@/lib/icons"

// Translations
import { useTrans, Trans } from "@/i18n"

// State
import { useDispatch, useSelector } from "@/state/hooks"
import {
	UserPreferencesState,
	setRatingModalDismissedUntil,
} from "@/state/features/userPreferencesSlice"

// User
import { useCurrentUserInvestmentsCount } from "@/context/user"

// Dates
import { useLocalizedDateTime } from "@/lib/dates"

// Analytics
import { sendEvent } from "@/lib/analytics"

// Environment
import { EXTERNAL_GOOGLE_REVIEW_URL } from "@/lib/env"

type Step = "ask" | "thanks_positive" | "thanks_negative"

export function RatingModal() {
	const t = useTrans("dashboard")
	const dispatch = useDispatch()
	const DateTime = useLocalizedDateTime()
	const investmentsCount = useCurrentUserInvestmentsCount()
	const dismissedUntil = useSelector(
		({ userPreferences }: { userPreferences: UserPreferencesState }) =>
			userPreferences.ratingModalDismissedUntil,
	)

	const [step, setStep] = useState<Step>("ask")
	const [rating, setRating] = useState(0)
	const [isClosed, setIsClosed] = useState(false)

	const isSnoozed =
		dismissedUntil !== null &&
		DateTime.fromISO(dismissedUntil) > DateTime.now()
	const shouldShow = investmentsCount > 0 && !isSnoozed && !isClosed

	function snoozeForAYear() {
		const oneYearFromNow = DateTime.now().plus({ years: 1 }).toISO()
		dispatch(setRatingModalDismissedUntil(oneYearFromNow))
	}

	function handleSubmit() {
		if (rating === 0) return
		sendEvent("rating_modal", "submit", { label: String(rating), rating })
		setStep(rating >= 4 ? "thanks_positive" : "thanks_negative")
	}

	function handleClose() {
		sendEvent("rating_modal", "dismiss", { label: `step:${step}`, step })
		snoozeForAYear()
		setIsClosed(true)
	}

	function handleGoogleReview() {
		sendEvent("rating_modal", "google_review_clicked")
		window.open(EXTERNAL_GOOGLE_REVIEW_URL, "_blank", "noopener,noreferrer")
	}

	if (!shouldShow) return null

	return (
		<>
			<div
				className="fixed inset-0 z-[149] bg-gray-500/75"
				aria-hidden="true"
			/>
			<DialogWrapper className="!top-0">
				<div className="relative p-6 sm:p-8">
					<button
						type="button"
						onClick={handleClose}
						aria-label={t("dashboard:rating_modal.aria.close")}
						className="focus-visible:ring-primary-500 absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-hidden focus-visible:ring-2"
					>
						<XIcon className="size-5" />
					</button>

					{step === "ask" && (
						<div className="text-center">
							<Heading as="h2" styleAs="h4">
								{t("dashboard:rating_modal.title")}
							</Heading>
							<p className="mt-2 text-sm text-gray-600">
								{t("dashboard:rating_modal.question")}
							</p>
							<div className="my-6">
								<StarRating
									value={rating}
									onChange={setRating}
								/>
							</div>
							<div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
								<Button
									variant="transparent"
									onClick={handleClose}
								>
									{t("dashboard:rating_modal.maybe_later")}
								</Button>
								<Button
									variant={
										rating === 0 ? "disabled" : "primary"
									}
									disabled={rating === 0}
									onClick={handleSubmit}
								>
									{t("dashboard:rating_modal.submit")}
								</Button>
							</div>
						</div>
					)}

					{step === "thanks_positive" && (
						<div className="text-center">
							<Heading as="h2" styleAs="h4">
								{t("dashboard:rating_modal.google_cta_title")}
							</Heading>
							<p className="my-4 text-sm text-gray-600">
								{t("dashboard:rating_modal.google_cta_body")}
							</p>
							<div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-center">
								<Button
									variant="transparent"
									onClick={handleClose}
								>
									{t("dashboard:rating_modal.close")}
								</Button>
								<Button onClick={handleGoogleReview}>
									{t(
										"dashboard:rating_modal.google_cta_button",
									)}
								</Button>
							</div>
						</div>
					)}

					{step === "thanks_negative" && (
						<div className="text-center">
							<Heading as="h2" styleAs="h4">
								{t("dashboard:rating_modal.unsatisfied_title")}
							</Heading>
							<p className="my-4 text-sm text-gray-600">
								<Trans
									ns="dashboard"
									i18nKey="dashboard:rating_modal.unsatisfied_body"
									components={{
										a: (
											<a
												href="mailto:info@zonhub.com"
												className="text-primary-500 underline"
											>
												info@zonhub.com
											</a>
										),
									}}
								/>
							</p>
							<Button onClick={handleClose}>
								{t("dashboard:rating_modal.close")}
							</Button>
						</div>
					)}
				</div>
			</DialogWrapper>
		</>
	)
}
